import { useEffect, useRef } from 'react'

/**
 * ATL-1179 — seed the New Sale cart from a client order EXACTLY ONCE.
 *
 * The seeding used to live inside `NewSaleScreen`'s big saved-carts effect, whose dependency
 * array contains `context.unsubscribeSavedCarts` — a value that SAME effect assigns a few lines
 * above via `setContext`. So the effect re-triggered itself, and while the subscription half was
 * guarded (`if (!context.unsubscribeSavedCarts)`), the seeding half was not:
 *
 *     if (clientOrderDetail) {
 *       newSaleFromClientOrder(clientOrderDetail)   // appends, no guard
 *     }
 *
 * `newSaleFromClientOrder` is a bare append loop, so every re-run added the order's lines again.
 * Measured on staging 2026-09-04, an order for 3 opened New Sale with 6 — identically on both read
 * paths, so this predates the Supabase cutover and is live for every merchant. It is the primary
 * button of the Customer Orders screen: the merchant sells double and decrements stock double.
 *
 * `store`, `context.db` and `organization?.supabaseId` also settle asynchronously and each flip
 * re-runs that effect, so "doubles" was only the count observed on a fast connection — the real
 * behaviour is "once per dependency settle", and a slower device can seed three times or more.
 *
 * ⚠️ THE GUARD IS THE REF, NOT THE DEPENDENCY ARRAY. Pinning a dependency list would be pinning
 * the symptom: any future dependency that settles late brings the bug straight back, and nothing
 * would fail. The invariant this hook holds is stronger and is what the tests assert — *however
 * many times the effect runs, one order is seeded once*.
 */

/**
 * The identity a seeded order is remembered by.
 *
 * Prefers the Firestore document id, which is what both read paths put on `id`. Falls back to the
 * object itself so the key is never `null` for a truthy order — `null` is the key of "no order",
 * so returning it for a real one would conflate the two.
 */
export const clientOrderSeedKey = (order) => order?.id ?? order?.firestoreId ?? order ?? null

/**
 * @param {object|null|undefined} clientOrderDetail  the order being converted, or nothing
 * @param {boolean} productsReady  whether the catalog the lines resolve against has loaded
 * @param {(order: object) => void} seedCart  performs the seeding (appends the order's lines)
 */
export function useClientOrderCartSeed({ clientOrderDetail, productsReady, seedCart }) {
  // A SET, not a single slot. A one-slot `seededKey` remembers only the LAST order, so the
  // sequence A -> B -> A would seed A a second time — which is the doubling this hook exists to
  // prevent, merely made harder to reach. Nothing in today's navigation can produce that
  // sequence (NewSale is always pushed fresh on top of ClientOrderDetail, so each conversion is
  // a new mount with an empty set), but the invariant this hook promises is "one order is seeded
  // once", and a guard should implement the invariant rather than the one path that reaches it.
  // Allocated lazily so it is not rebuilt and thrown away on every render.
  const seededKeysRef = useRef(null)

  useEffect(() => {
    if (!clientOrderDetail) return

    /*
     * Products are the seed's PRECONDITION, not an optimisation: `newSaleFromClientOrder`
     * resolves every line as `productsDict[item.id]` and `addNewCartItem` dereferences the result
     * immediately. Seeding before the catalog loads would put an EMPTY cart on screen and — being
     * once-only — never retry, which is quieter than the doubling it replaces and harder to
     * notice. Waiting costs nothing: the effect re-runs when `productsReady` flips.
     */
    if (!productsReady) return

    const key = clientOrderSeedKey(clientOrderDetail)
    const seededKeys = seededKeysRef.current ?? (seededKeysRef.current = new Set())
    if (seededKeys.has(key)) return

    /*
     * Marked BEFORE seeding, deliberately. A line referencing a deleted product makes
     * `addNewCartItem` dereference `undefined` (ATL-1178 — 33% of orders fleet-wide); because
     * `newSaleFromClientOrder` is `async`, that surfaces as a REJECTED PROMISE rather than a
     * synchronous throw, so the effect returns normally and the cart is left partial. Marking
     * afterwards would let a re-run re-append everything before the bad line, growing the cart on
     * each pass. Marking first turns that into a single partial cart — today's outcome minus the
     * growth.
     *
     * The rejection is deliberately not caught here: whether a deleted product should skip, warn
     * or refuse is ATL-1178's decision, and swallowing it would quietly make that decision.
     */
    seededKeys.add(key)
    seedCart(clientOrderDetail)
  }, [clientOrderDetail, productsReady, seedCart])
}
